// 由 scripts/extract-legacy-search-forms.ts 產生，不要手改。
// 重跑：bunx tsx scripts/extract-legacy-search-forms.ts [rawDomLegacyRoot]
//
// 來源：.tmp/ui/raw-dom/legacy/admin/<module>/<pageId>.html（原系統實測 DOM，唯讀基準證據）。
//       78 頁有查詢／搜尋表單，共 462 組欄位、566 個控制項，
//       其中 37 個 select 的 option 值域通過 optionsAreEnum() 而被抽下來。
//
// 這裡只有**欄位骨架**：label 文字、控制項種類、name、type、長度上限、
// readonly/required、radio/checkbox 的送出代碼與預設勾選、固定列舉下拉的 option。
// text/number/file/textarea 的 value 一個都沒有（TEST-DATA-AND-PII-POLICY §4），
// input[type=hidden] 與動作鈕整個不抽。

export interface LegacySearchOption {
  readonly value: string;
  readonly text: string;
  readonly selected?: boolean;
}

export interface LegacySearchControl {
  readonly kind: 'input' | 'select' | 'textarea' | 'choice' | 'text';
  /** kind==='text' 時的裸文字（例如兩個日期欄位之間的 ~）；kind==='choice' 時的選項文字。 */
  readonly text?: string;
  readonly name?: string;
  readonly type?: string;
  readonly maxLength?: number;
  readonly required?: boolean;
  readonly readOnly?: boolean;
  readonly placeholder?: string;
  /** radio／checkbox 的送出代碼。 */
  readonly value?: string;
  readonly checked?: boolean;
  /** 原系統該下拉的 option 數（不論有沒有抽 options）。 */
  readonly optionCount?: number;
  /** 只有固定列舉下拉才有；外鍵下拉一律 undefined。 */
  readonly options?: readonly LegacySearchOption[];
  /** 原系統該控制項帶了本抽取器不重現的 value 屬性（日期預設值等）。 */
  readonly hasUnreproducibleValue?: boolean;
}

export interface LegacySearchGroup {
  /** 欄位標籤原文（含全形冒號與必填星號）；原系統該組沒有 label 時是空字串。 */
  readonly label: string;
  readonly controls: readonly LegacySearchControl[];
}

export interface LegacySearchForm {
  /** 原系統該表單的 id（queryForm／searchForm…），供追溯。 */
  readonly formId: string;
  readonly groups: readonly LegacySearchGroup[];
}

/** pageId → 原系統該頁查詢表單的篩選欄位，順序與畫面一致。 */
export const LEGACY_SEARCH_FORMS: Readonly<Record<string, LegacySearchForm>> = {
  'announcement-announcement-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 公告類型：',
        controls: [
          { kind: 'select', name: 'type', required: true, optionCount: 7, options: [{ value: '-1', text: '全部' }, { value: '0', text: '全院公告' }, { value: '1', text: '系統公告' }, { value: '2', text: '部門公告' }, { value: '3', text: '訓練類別公告' }, { value: '4', text: '計畫公告' }, { value: '5', text: '課程公告' }] },
        ],
      },
      {
        label: '',
        controls: [
          { kind: 'input', name: 'accountId', type: 'text' },
        ],
      },
      {
        label: '* 公告單位：',
        controls: [
          { kind: 'select', name: 'refId', optionCount: 1 },
        ],
      },
      {
        label: '* 公告對象：',
        controls: [
          { kind: 'select', name: 'object', optionCount: 2, options: [{ value: '-1', text: '全部' }, { value: '0', text: '全院' }] },
        ],
      },
      {
        label: '* 公告狀態：',
        controls: [
          { kind: 'select', name: 'status', optionCount: 4, options: [{ value: '-1', text: '全部' }, { value: '0', text: '一般' }, { value: '1', text: '公開' }, { value: '2', text: '置頂' }] },
        ],
      },
      {
        label: '* 有效狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'enableStatus', type: 'radio', value: '0' },
          { kind: 'choice', text: '公告中', name: 'enableStatus', type: 'radio', value: '1', checked: true },
          { kind: 'choice', text: '待公告', name: 'enableStatus', type: 'radio', value: '2' },
          { kind: 'choice', text: '已過期', name: 'enableStatus', type: 'radio', value: '3' },
        ],
      },
    ],
  },
  'config-account-v-2': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '二級單位：',
        controls: [
          { kind: 'select', name: 'secondDepartmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '角色：',
        controls: [
          { kind: 'select', name: 'roleId', optionCount: 10 },
        ],
      },
      {
        label: '狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用', selected: true }, { value: '0', text: '停用' }] },
        ],
      },
      {
        label: '關鍵字：',
        controls: [
          { kind: 'input', name: 'keyword', type: 'text', maxLength: 20, placeholder: '可為姓名、員編或學號' },
        ],
      },
    ],
  },
  'config-comment': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 類別：',
        controls: [
          { kind: 'select', name: 'type', required: true, optionCount: 1, options: [{ value: '-1', text: '全部' }] },
        ],
      },
      {
        label: '* 狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', required: true, optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用', selected: true }, { value: '0', text: '停用' }] },
        ],
      },
      {
        label: '常用語：',
        controls: [
          { kind: 'input', name: 'selePrivate', type: 'checkbox' },
        ],
      },
    ],
  },
  'config-job-type': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
    ],
  },
  'course-clazz-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 期間：',
        controls: [
          { kind: 'input', name: 'beginDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: '匯入課程：',
        controls: [
          { kind: 'input', name: 'uploadFile', type: 'file' },
          { kind: 'text', text: '說明:' },
        ],
      },
    ],
  },
  'course-clazz-query-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 期間：',
        controls: [
          { kind: 'input', name: 'beginDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
    ],
  },
  'course-clazz-query-list-class-query': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 期間：',
        controls: [
          { kind: 'input', name: 'beginDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
    ],
  },
  'course-course-manage': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
    ],
  },
  'course-course-static-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 3 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'input', name: 'caseTypeDisplay', type: 'text' },
        ],
      },
      {
        label: '* 課程日期：',
        controls: [
          { kind: 'input', name: 'queryStartDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'queryStartDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'course-course-type': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 3 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'enableStatus', optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用' }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'customer-tsgh-case-report': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段部門：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'customer-tsgh-core-curriculum-report': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 組別：',
        controls: [
          { kind: 'select', name: 'groupId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'planStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'planEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '* 查詢結果：',
        controls: [
          { kind: 'choice', text: '依訓練計畫', name: 'resultType', type: 'radio', value: 'plan', checked: true },
          { kind: 'choice', text: '依訓練月份', name: 'resultType', type: 'radio', value: 'month' },
        ],
      },
    ],
  },
  'customer-tsgh-course-record-report': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段部門：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'planStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'planEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'customer-tsgh-high-risk-doctor-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 表單發送期間(起迄)：',
        controls: [
          { kind: 'input', name: 'startDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'customer-tsgh-high-risk-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 表單發送期間(起迄)：',
        controls: [
          { kind: 'input', name: 'startDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'customer-tsgh-service-standard-cfg-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
    ],
  },
  'customer-tsgh-service-standard-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 表單發送期間(起迄)：',
        controls: [
          { kind: 'input', name: 'startDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'customer-tsgh-tch-satisfaction-survey-report': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 2 },
        ],
      },
      {
        label: '* 期間：',
        controls: [
          { kind: 'input', name: 'startDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
      {
        label: '* 教師：',
        controls: [
          { kind: 'choice', text: '全部', name: 'teacherType', type: 'radio', value: '1', checked: true },
          { kind: 'choice', text: '主治醫師', name: 'teacherType', type: 'radio', value: '2' },
          { kind: 'choice', text: '住院醫師', name: 'teacherType', type: 'radio', value: '3' },
        ],
      },
    ],
  },
  'customer-tsgh-tsgh-student-course-report': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段部門：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'customer-tsgh-tsgh-study-history-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'discuss-discuss-board': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 留言類別：',
        controls: [
          { kind: 'select', name: 'discussTypeId', optionCount: 1 },
        ],
      },
      {
        label: '日期：',
        controls: [
          { kind: 'input', name: 'beginDateStr', type: 'text', maxLength: 10, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'endDateStr', type: 'text', maxLength: 10, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: '關鍵字：',
        controls: [
          { kind: 'input', name: 'content', type: 'text' },
        ],
      },
      {
        label: '狀態：',
        controls: [
          { kind: 'select', name: 'status', optionCount: 3, options: [{ value: '', text: '全部' }, { value: '1', text: '公開' }, { value: '2', text: '隱藏' }] },
        ],
      },
    ],
  },
  'discuss-discuss-type': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'enableStatus', optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用' }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'exam-dispatch-exam': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '* 測試目標：',
        controls: [
          { kind: 'select', name: 'examTargetId', optionCount: 1 },
        ],
      },
      {
        label: '* 分派狀態：',
        controls: [
          { kind: 'select', name: 'dispatchStatus', required: true, optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '0', text: '未分派' }, { value: '1', text: '已分派' }] },
        ],
      },
    ],
  },
  'exam-exam-manage': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
    ],
  },
  'exam-question-bank': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
    ],
  },
  'exam-question-bank-type': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
    ],
  },
  'exam-testing-manage': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '* 測試目標：',
        controls: [
          { kind: 'select', name: 'examTargetId', optionCount: 1 },
        ],
      },
      {
        label: '測驗狀態：',
        controls: [
          { kind: 'select', name: 'testingStatus', optionCount: 6, options: [{ value: '-1', text: '全部' }, { value: '4', text: '逾期未作答' }, { value: '0', text: '未作答' }, { value: '1', text: '作答中' }, { value: '2', text: '待審核' }, { value: '3', text: '已完成' }] },
        ],
      },
    ],
  },
  'form-all-form-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobtypeId', optionCount: 2 },
        ],
      },
      {
        label: '表單用途：',
        controls: [
          { kind: 'select', name: 'purpose', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'plan', text: '計畫表單' }, { value: 'class', text: '課程表單' }] },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 3 },
        ],
      },
      {
        label: '表單名稱：',
        controls: [
          { kind: 'input', name: 'formName', type: 'text' },
        ],
      },
      {
        label: '* 表單發送日：',
        controls: [
          { kind: 'input', name: 'queryStartDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'queryStartDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '關鍵字：',
        controls: [
          { kind: 'input', name: 'keyword', type: 'text' },
        ],
      },
    ],
  },
  'form-class-form-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '表單名稱：',
        controls: [
          { kind: 'select', name: 'formDepartmentId', optionCount: 3 },
        ],
      },
      {
        label: '填表者：',
        controls: [
          { kind: 'input', name: 'searchFillerCode', type: 'text', placeholder: '(請輸入填表者姓名或員編)' },
          { kind: 'select', name: 'fillerId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入姓名或編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '* 課程期間：',
        controls: [
          { kind: 'input', name: 'classStartDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'classEndDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'enableStatus', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'true', text: '啟用', selected: true }, { value: 'false', text: '停用' }] },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formStatus', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formStatus', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formStatus', type: 'radio', value: 'true' },
        ],
      },
      {
        label: '自己待填的表單：',
        controls: [
          { kind: 'input', type: 'checkbox' },
          { kind: 'text', text: '(只顯示自己待填的表單)' },
        ],
      },
    ],
  },
  'form-co-edit-form-completion-statistics-page': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annual', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organization', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobType', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTarget', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 共同編輯表單：',
        controls: [
          { kind: 'select', name: 'formDepartment', optionCount: 2 },
          { kind: 'select', name: 'formTemplate', optionCount: 1 },
        ],
      },
      {
        label: '* 課程日期：',
        controls: [
          { kind: 'input', name: 'classDateStart', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'classDateEnd', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
      {
        label: '學員姓名：',
        controls: [
          { kind: 'input', name: 'keyword', type: 'text' },
          { kind: 'select', name: 'account', optionCount: 0 },
        ],
      },
    ],
  },
  'form-co-edit-form-export-page': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annual', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organization', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobType', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTarget', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 共同編輯表單：',
        controls: [
          { kind: 'select', name: 'formDepartment', optionCount: 1 },
          { kind: 'select', name: 'formTemplate', optionCount: 0 },
        ],
      },
      {
        label: '* 課程日期：',
        controls: [
          { kind: 'input', name: 'classDateStart', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'classDateEnd', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
      {
        label: '學員姓名：',
        controls: [
          { kind: 'input', name: 'keyword', type: 'text' },
          { kind: 'select', name: 'account', optionCount: 0 },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formDone', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formDone', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formDone', type: 'radio', value: 'true' },
        ],
      },
    ],
  },
  'form-co-edit-form-form-generator': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organization', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobType', optionCount: 2 },
        ],
      },
      {
        label: '* 表單隸屬部門：',
        controls: [
          { kind: 'select', name: 'formDeptId', optionCount: 2 },
        ],
      },
      {
        label: '* 共同編輯表單：',
        controls: [
          { kind: 'select', name: 'formtemplateId', optionCount: 0 },
        ],
      },
    ],
  },
  'form-co-edit-form-query-page': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annual', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organization', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobType', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTarget', required: true, optionCount: 2 },
        ],
      },
      {
        label: '表單名稱：',
        controls: [
          { kind: 'select', name: 'formDepartment', optionCount: 2 },
        ],
      },
      {
        label: '參與表單填寫員：',
        controls: [
          { kind: 'input', name: 'keyword', type: 'text' },
          { kind: 'select', name: 'account', optionCount: 0 },
        ],
      },
      {
        label: '* 課程日期：',
        controls: [
          { kind: 'input', name: 'classDateStart', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'classDateEnd', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'enableType', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'ENABLE', text: '啟用', selected: true }, { value: 'DISABLE', text: '停用' }] },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formDone', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formDone', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formDone', type: 'radio', value: 'true' },
        ],
      },
      {
        label: '自己待填的表單：',
        controls: [
          { kind: 'input', type: 'checkbox' },
        ],
      },
    ],
  },
  'form-form-static-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobtypeId', optionCount: 2 },
        ],
      },
      {
        label: '表單用途：',
        controls: [
          { kind: 'select', name: 'purpose', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'plan', text: '計畫表單' }, { value: 'class', text: '課程表單' }] },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 0 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 3 },
        ],
      },
      {
        label: '表單名稱：',
        controls: [
          { kind: 'input', name: 'formName', type: 'text' },
        ],
      },
      {
        label: '* 表單發送日：',
        controls: [
          { kind: 'input', name: 'queryStartDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'queryStartDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'form-plan-form-batch-send': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段部門：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫表單：',
        controls: [
          { kind: 'select', name: 'formTemplateId', required: true, optionCount: 0 },
        ],
      },
    ],
  },
  'form-plan-form-dispatch': {
    formId: 'queryForm',
    groups: [
      {
        label: '目標日期：',
        controls: [
          { kind: 'input', name: 'targetDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
    ],
  },
  'form-plan-form-list': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '訓練階段科部：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', optionCount: 0 },
        ],
      },
      {
        label: '訓練階段類型：',
        controls: [
          { kind: 'select', name: 'phaseType', optionCount: 7, options: [{ value: '', text: '全部' }, { value: 'Plan', text: '計劃' }, { value: 'Group', text: '組別' }, { value: 'Phase', text: '階段' }, { value: 'SubPhase', text: '子階段' }, { value: 'RefElective1', text: '選修1' }, { value: 'RefElective2', text: '選修2' }] },
        ],
      },
      {
        label: '表單名稱：',
        controls: [
          { kind: 'select', name: 'formDepartmentId', optionCount: 3 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'trainingStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'trainingEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '填表者：',
        controls: [
          { kind: 'input', name: 'searchFillerCode', type: 'text', placeholder: '(請輸入填表者姓名或員編)' },
          { kind: 'select', name: 'fillerId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'enableStatus', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'true', text: '啟用', selected: true }, { value: 'false', text: '停用' }] },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formStatus', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formStatus', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formStatus', type: 'radio', value: 'true' },
        ],
      },
      {
        label: '自己待填的表單：',
        controls: [
          { kind: 'input', type: 'checkbox' },
          { kind: 'text', text: '(只顯示自己待填的表單)' },
        ],
      },
    ],
  },
  'form-template-common-form-template': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '用途：',
        controls: [
          { kind: 'select', name: 'purpose', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'plan', text: '訓練計畫' }, { value: 'class', text: '課程' }] },
        ],
      },
    ],
  },
  'instruction-plan-target-plan-target-list': {
    formId: 'searchPlanTargetForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 1 },
        ],
      },
      {
        label: '部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
    ],
  },
  'instruction-study-item-maintain': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 學習項目類別：',
        controls: [
          { kind: 'select', name: 'studyItemTypeId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', required: true, optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用', selected: true }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'instruction-study-item-type-maintain': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '啟用狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用', selected: true }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'instruction-teacher-pool-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '啟用/停用狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', optionCount: 3, options: [{ value: '-1', text: '全部' }, { value: '1', text: '啟用', selected: true }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'leave-leave-annual': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'leave-leave-form': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '部門：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'accountId', optionCount: 0 },
        ],
      },
      {
        label: '狀態：',
        controls: [
          { kind: 'select', name: 'status', optionCount: 4, options: [{ value: '', text: '全部' }, { value: '0', text: '審核中' }, { value: '1', text: '通過' }, { value: '-1', text: '未通過' }] },
        ],
      },
    ],
  },
  'leave-leave-type': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 啟用狀態：',
        controls: [
          { kind: 'select', name: 'isEnable', required: true, optionCount: 3, options: [{ value: '-1', text: '全部', selected: true }, { value: '1', text: '啟用' }, { value: '0', text: '停用' }] },
        ],
      },
    ],
  },
  'report-common-class-form-export': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 0 },
        ],
      },
      {
        label: '* 表單名稱：',
        controls: [
          { kind: 'select', name: 'formDepartmentId', optionCount: 3 },
        ],
      },
      {
        label: '* 課程期間：',
        controls: [
          { kind: 'input', name: 'classStartDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
          { kind: 'input', name: 'classEndDate', type: 'text', maxLength: 10, required: true, readOnly: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: '評分顯示方式：',
        controls: [
          { kind: 'choice', text: '文字描述', name: 'isShowScore', type: 'radio', value: 'false', checked: true },
          { kind: 'choice', text: '分數', name: 'isShowScore', type: 'radio', value: 'true' },
        ],
      },
      {
        label: '額外資訊：',
        controls: [
          { kind: 'choice', text: '評核者', type: 'checkbox' },
          { kind: 'choice', text: '受評者', type: 'checkbox' },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formStatus', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formStatus', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formStatus', type: 'radio', value: 'true' },
        ],
      },
    ],
  },
  'report-common-clazz-attendance-export': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 1 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 課程期間：',
        controls: [
          { kind: 'input', name: 'beginDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'report-common-clazz-detail-export': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 1 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 3, options: [{ value: '', text: '全部' }, { value: 'class', text: '課程' }, { value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 課程期間：',
        controls: [
          { kind: 'input', name: 'beginDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'endDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'report-common-plan-form-export': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '訓練階段科部：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '訓練階段類型：',
        controls: [
          { kind: 'select', name: 'phaseType', optionCount: 8, options: [{ value: '', text: '全部' }, { value: 'Plan', text: '計劃' }, { value: 'Group', text: '組別' }, { value: 'Phase', text: '階段' }, { value: 'SubPhase', text: '子階段' }, { value: 'RefElective1', text: '選修1' }, { value: 'RefElective2', text: '選修2' }, { value: 'community', text: '外訓醫院' }] },
        ],
      },
      {
        label: '* 表單名稱：',
        controls: [
          { kind: 'select', name: 'formDepartmentId', optionCount: 3 },
        ],
      },
      {
        label: '',
        controls: [
          { kind: 'select', name: 'formTemplateId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'trainingStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'trainingEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '評分顯示方式：',
        controls: [
          { kind: 'choice', text: '文字描述', name: 'isShowScore', type: 'radio', value: 'false', checked: true },
          { kind: 'choice', text: '分數', name: 'isShowScore', type: 'radio', value: 'true' },
        ],
      },
      {
        label: '額外資訊：',
        controls: [
          { kind: 'choice', text: '評核者', type: 'checkbox' },
          { kind: 'choice', text: '受評者', type: 'checkbox' },
        ],
      },
      {
        label: '表單進行狀態：',
        controls: [
          { kind: 'choice', text: '全部', name: 'formStatus', type: 'radio', value: '', checked: true },
          { kind: 'choice', text: '進行中', name: 'formStatus', type: 'radio', value: 'false' },
          { kind: 'choice', text: '已完成', name: 'formStatus', type: 'radio', value: 'true' },
        ],
      },
    ],
  },
  'report-pgywm-all-student-scores': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練計畫名稱：',
        controls: [
          { kind: 'select', optionCount: 0 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'report-tsgh-academic-mom': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', optionCount: 0 },
        ],
      },
      {
        label: '* 課程月份：',
        controls: [
          { kind: 'input', name: 'StartDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'EndDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
      {
        label: '* 表單進行狀態：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
    ],
  },
  'report-tsgh-class-form-static': {
    formId: 'queryForm',
    groups: [
      {
        label: '教師：',
        controls: [
          { kind: 'select', name: 'teacherId', optionCount: 1 },
        ],
      },
      {
        label: '課程：',
        controls: [
          { kind: 'select', name: 'classId', optionCount: 1 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-class-form-static-to-class-form-7': {
    formId: 'queryForm',
    groups: [
      {
        label: '教師：',
        controls: [
          { kind: 'select', name: 'teacherId', optionCount: 1 },
        ],
      },
      {
        label: '課程：',
        controls: [
          { kind: 'select', name: 'classId', optionCount: 1 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-core-skill-form-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId2', optionCount: 2 },
        ],
      },
      {
        label: '醫院：',
        controls: [
          { kind: 'select', name: 'organizationId2', optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId2', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId2', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId2', optionCount: 2 },
        ],
      },
    ],
  },
  'report-tsgh-core-skill-report-by-form': {
    formId: 'searchForm',
    groups: [
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId', optionCount: 0 },
        ],
      },
      {
        label: '醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 0 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 0 },
        ],
      },
    ],
  },
  'report-tsgh-core-skill-report-by-student': {
    formId: 'searchForm',
    groups: [
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId', optionCount: 0 },
        ],
      },
      {
        label: '醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 0 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 0 },
        ],
      },
    ],
  },
  'report-tsgh-core-skill-statistic': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 核心技能低標數值：',
        controls: [
          { kind: 'input', name: 'min', type: 'number', required: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: '* 核心技能高標數值：',
        controls: [
          { kind: 'input', name: 'max', type: 'number', required: true, hasUnreproducibleValue: true },
        ],
      },
      {
        label: 'EXCEL匯入名單：',
        controls: [
          { kind: 'input', name: 'uploadExcel', type: 'file' },
        ],
      },
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId2', optionCount: 2 },
        ],
      },
      {
        label: '醫院：',
        controls: [
          { kind: 'select', name: 'organizationId2', optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId2', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId2', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId2', optionCount: 2 },
        ],
      },
    ],
  },
  'report-tsgh-core-skill-sutdent-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId2', optionCount: 2 },
        ],
      },
      {
        label: '醫院：',
        controls: [
          { kind: 'select', name: 'organizationId2', optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId2', optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId2', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId2', optionCount: 2 },
        ],
      },
    ],
  },
  'report-tsgh-gpa-form-report': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練計畫名稱：',
        controls: [
          { kind: 'select', optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段科部：',
        controls: [
          { kind: 'select', optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段類型：',
        controls: [
          { kind: 'select', name: 'phaseType', optionCount: 7, options: [{ value: '', text: '全部' }, { value: 'Plan', text: '計劃' }, { value: 'Group', text: '組別' }, { value: 'Phase', text: '階段' }, { value: 'SubPhase', text: '子階段' }, { value: 'RefElective1', text: '選修1' }, { value: 'RefElective2', text: '選修2' }] },
        ],
      },
      {
        label: '* 表單名稱：',
        controls: [
          { kind: 'select', name: 'formTemplateId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練期間：',
        controls: [
          { kind: 'input', name: 'trainingStartDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'input', name: 'trainingEndDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'report-tsgh-login-static': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 登入時間：',
        controls: [
          { kind: 'input', name: 'logDateStart', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'logDateEnd', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-plan-form-static': {
    formId: 'queryForm',
    groups: [
      {
        label: '教師：',
        controls: [
          { kind: 'select', name: 'teacherId', optionCount: 1 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-plan-form-static-2': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 訓練階段科部：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-plan-form-static-2-to-plan-form-10': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 訓練階段科部：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-plan-form-static-2-to-plan-form-9': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 訓練階段科部：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-tsgh-plan-form-static-to-plan-form-5': {
    formId: 'queryForm',
    groups: [
      {
        label: '教師：',
        controls: [
          { kind: 'select', name: 'teacherId', optionCount: 1 },
        ],
      },
      {
        label: '* 表單時間：',
        controls: [
          { kind: 'input', name: 'submitDateS', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'submitDateE', type: 'text', maxLength: 10, required: true },
        ],
      },
    ],
  },
  'report-ugywm-course-form-finishrate': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', optionCount: 4 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 類型：',
        controls: [
          { kind: 'select', name: 'kind', optionCount: 1, options: [{ value: 'meeting', text: '會議' }] },
        ],
      },
      {
        label: '* 課程類別：',
        controls: [
          { kind: 'select', name: 'courseTypeId', optionCount: 0 },
        ],
      },
      {
        label: '* 期間：',
        controls: [
          { kind: 'input', name: 'StartDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'EndDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'report-ugywm-student-dept-satisfy': {
    formId: 'searchForm',
    groups: [
      {
        label: '學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 3 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 1 },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練期間(起迄)：',
        controls: [
          { kind: 'input', name: 'trainingStartDate', type: 'text', maxLength: 10, required: true, readOnly: true },
          { kind: 'text', text: '~' },
          { kind: 'input', name: 'trainingEndDate', type: 'text', maxLength: 10, required: true, readOnly: true },
        ],
      },
    ],
  },
  'system-api-log': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 請求時間：',
        controls: [
          { kind: 'input', name: 'queryForm_startDate', type: 'text', required: true, readOnly: true },
          { kind: 'input', name: 'queryForm_endDate', type: 'text', required: true, readOnly: true },
        ],
      },
    ],
  },
  'system-login-log': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 登入日期：',
        controls: [
          { kind: 'input', name: 'queryForm_startDate', type: 'text', required: true, readOnly: true },
          { kind: 'input', name: 'queryForm_endDate', type: 'text', required: true, readOnly: true },
        ],
      },
      {
        label: '登入狀態：',
        controls: [
          { kind: 'select', optionCount: 3 },
        ],
      },
    ],
  },
  'training-arrange-train': {
    formId: 'searchForm',
    groups: [
      {
        label: '* 年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練階段執行部門：',
        controls: [
          { kind: 'select', name: 'phaseDepartmentId', optionCount: 2 },
        ],
      },
      {
        label: '子階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '訓練階段起訖：',
        controls: [
          { kind: 'input', name: 'phaseStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'phaseEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '臨床教師：',
        controls: [
          { kind: 'input', type: 'text', placeholder: '(請輸入姓名或員編)' },
          { kind: 'select', name: 'clinicalId', optionCount: 0 },
        ],
      },
      {
        label: '階段導師：',
        controls: [
          { kind: 'input', type: 'text', placeholder: '(請輸入姓名或員編)' },
          { kind: 'select', name: 'mentorId', optionCount: 0 },
        ],
      },
    ],
  },
  'training-permission-manage-student': {
    formId: 'permissionManageSearchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 2 },
        ],
      },
      {
        label: '角色：',
        controls: [
          { kind: 'select', name: 'roleId', optionCount: 2 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'trainingStartDate', type: 'text', maxLength: 10, readOnly: true },
          { kind: 'input', name: 'trainingEndDate', type: 'text', maxLength: 10, readOnly: true },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchNo', type: 'text', placeholder: '(請輸入姓名/員編)' },
          { kind: 'select', name: 'accountId', optionCount: 0 },
        ],
      },
    ],
  },
  'training-permission-manage-teacher': {
    formId: 'permissionManageSearchForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', optionCount: 1 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 部門：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '角色：',
        controls: [
          { kind: 'select', name: 'roleId', optionCount: 7 },
        ],
      },
    ],
  },
  'training-plan-inst-clinical-arrange': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', optionCount: 2 },
        ],
      },
      {
        label: '訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 0 },
        ],
      },
      {
        label: '* 臨床教師科部：',
        controls: [
          { kind: 'select', name: 'clinicalDepartmentId', required: true, optionCount: 3 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'phaseStartDate', type: 'text', maxLength: 10, required: true },
          { kind: 'input', name: 'phaseEndDate', type: 'text', maxLength: 10, required: true },
        ],
      },
      {
        label: '臨床教師姓名：',
        controls: [
          { kind: 'input', name: 'clinicalName', type: 'text' },
        ],
      },
      {
        label: '學員姓名：',
        controls: [
          { kind: 'input', name: 'studentName', type: 'text' },
        ],
      },
    ],
  },
  'training-plan-inst-plan-inst-list': {
    formId: 'searchPlanInstForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入姓名或員工編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '導師：',
        controls: [
          { kind: 'input', name: 'searchMentorNo', type: 'text', placeholder: '(請輸入姓名或員工編號)' },
          { kind: 'select', name: 'mentorId', optionCount: 0 },
        ],
      },
      {
        label: '臨床教師：',
        controls: [
          { kind: 'input', type: 'text', placeholder: '(請輸入姓名或員工編號)' },
          { kind: 'select', name: 'clinicalId', optionCount: 0 },
        ],
      },
    ],
  },
  'training-study-passport-study-passport-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 科部：',
        controls: [
          { kind: 'select', name: 'departmentId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '組別：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
    ],
  },
  'training-study-record-study-record-fill': {
    formId: 'queryForm',
    groups: [
      {
        label: '',
        controls: [
          { kind: 'text', text: '需選擇學習目標後方數字相同的項目' },
        ],
      },
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
    ],
  },
  'training-study-record-study-record-status': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 訓練階段：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', required: true, optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'searchStudentCode', type: 'text', placeholder: '(請輸入學員姓名或員編)' },
          { kind: 'select', name: 'studentId', optionCount: 0 },
        ],
      },
      {
        label: '完成狀態：',
        controls: [
          { kind: 'select', name: 'finishStatus', optionCount: 3, options: [{ value: 'ALL', text: '全部' }, { value: 'DONE', text: '己完成' }, { value: 'NOT', text: '未完成' }] },
        ],
      },
    ],
  },
  'training-transcript-list': {
    formId: 'queryForm',
    groups: [
      {
        label: '* 學年度：',
        controls: [
          { kind: 'select', name: 'annualId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 醫院：',
        controls: [
          { kind: 'select', name: 'organizationId', required: true, optionCount: 1 },
        ],
      },
      {
        label: '* 訓練類別：',
        controls: [
          { kind: 'select', name: 'jobTypeId', required: true, optionCount: 2 },
        ],
      },
      {
        label: '* 臨床訓練計畫：',
        controls: [
          { kind: 'select', name: 'planTargetId', optionCount: 0 },
          { kind: 'choice', text: '臨床教師部門：', name: 'queryDeptType', type: 'radio', value: '0' },
          { kind: 'select', name: 'clinicalDeptId', optionCount: 2 },
          { kind: 'choice', text: '訓練階段部門：', name: 'queryDeptType', type: 'radio', value: '1' },
          { kind: 'select', name: 'phaseDepartmentId', optionCount: 0 },
        ],
      },
      {
        label: '* 訓練階段名稱：',
        controls: [
          { kind: 'select', name: 'phaseTargetId', optionCount: 0 },
        ],
      },
      {
        label: '學員：',
        controls: [
          { kind: 'input', name: 'userCode', type: 'text', placeholder: '(請輸入學員姓名或學員編號)' },
          { kind: 'select', name: 'userId', optionCount: 0 },
        ],
      },
      {
        label: '訓練期間：',
        controls: [
          { kind: 'input', name: 'phaseStartDate', type: 'text', maxLength: 10 },
          { kind: 'input', name: 'phaseEndDate', type: 'text', maxLength: 10 },
        ],
      },
      {
        label: '',
        controls: [
          { kind: 'text', text: '說明:' },
        ],
      },
    ],
  },
};
